import { skipToken } from '@reduxjs/toolkit/query/react'
import { useFetchInfiniteInfluencerContentQuery } from '@/api/influencer-api'
import { useFetchInfiniteKolsContentQuery } from '@/api/k6-kols-api'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchKolContentInfiniteQuery } from '@/api/workspace-api/kol-api'
import { FetchKolContentPayload } from '@/api/workspaces-api'
import useIsPublicDetailPage from '@/hooks/use-is-public-detail-page'
import { usePermissions } from '@/hooks/use-permissions'
import { FetchKolContentResponse } from '@/types/api/kols'
import { Optional } from '@/types/optional'
import { PlatformShortcode } from '@/utils/convert-platform'

type UseKolPosts = (params: {
  kolId?: string
  kolDcIdHashed?: string
  platform?: PlatformShortcode
  request?: Omit<FetchKolContentPayload, 'platform'>
}) => {
  response?: FetchKolContentResponse
  isFetching: boolean
}

const useKolPosts: UseKolPosts = ({
  kolId,
  kolDcIdHashed,
  platform,
  request,
}) => {
  const isPublicKolDetailPage = useIsPublicDetailPage()
  const { withKolPermission } = usePermissions()

  const requestParams: Omit<FetchKolContentPayload, 'platform'> = {
    next_paging_token: '',
    ...request,
  }

  const { data: postsForPublic, isFetching: isFetchingPostsForPublic } =
    useFetchInfiniteKolsContentQuery(
      isPublicKolDetailPage && kolId
        ? { uuid: kolId, platform, ...requestParams }
        : skipToken,
    )
  const { data: postsForKol, isFetching: isFetchingPostsForKol } =
    useFetchInfiniteInfluencerContentQuery(
      withKolPermission && kolId
        ? {
            uuid: kolId,
            platform,
            ...requestParams,
          }
        : skipToken,
    )
  const { data: userStatus, isFetching: isFetchingUserStatus } =
    useFetchUserStatusQuery(
      withKolPermission || isPublicKolDetailPage ? skipToken : undefined,
    )
  const { data: postsForAd, isFetching: isFetchingPostsForAd } =
    useFetchKolContentInfiniteQuery(
      !withKolPermission &&
        !isPublicKolDetailPage &&
        userStatus?.currentWorkspaceId &&
        kolId
        ? {
            workspaceId: userStatus.currentWorkspaceId,
            radar_uuid: kolId,
            dc_id_hashed: kolDcIdHashed,
            platform,
            ...requestParams,
          }
        : skipToken,
    )

  const response = ((): Optional<FetchKolContentResponse> => {
    if (isPublicKolDetailPage) {
      return postsForPublic
    }

    if (withKolPermission) {
      return postsForKol
    }

    return postsForAd
  })()
  const isFetching = ((): boolean => {
    if (isPublicKolDetailPage) {
      return isFetchingPostsForPublic
    }

    if (withKolPermission) {
      return isFetchingPostsForKol
    }

    return isFetchingPostsForAd || isFetchingUserStatus
  })()

  return {
    response,
    isFetching,
  }
}

export default useKolPosts
